/**
 * 🔄 VEHICLE SUBSTITUTION SERVICE
 * Integration with vehicle substitution request APIs
 */

import { getApiBaseUrl } from '@/config/environment';


export interface SubstitutionVehicle {
  id: string;
  name: string; // e.g., "LEXUS GX 2023" - will be parsed into make/model/year
  image?: string;
  // These fields may not come from backend, so we'll parse from name
  make?: string;
  model?: string;
  year?: number;
  color?: string;
  licensePlate?: string;
  verificationStatus?: 'Verified' | 'Pending';
  type?: string;
}

// Helper to parse vehicle name into components
export const parseVehicleName = (name: string): { make: string; model: string; year: string } => {
  const parts = name.trim().split(' ');
  if (parts.length < 2) {
    return { make: name, model: '', year: '' };
  }

  // Last part is usually the year
  const lastPart = parts[parts.length - 1];
  const isYear = /^\d{4}$/.test(lastPart);

  if (isYear) {
    const year = lastPart;
    const make = parts[0];
    const model = parts.slice(1, -1).join(' ');
    return { make, model, year };
  } else {
    // No year found
    const make = parts[0];
    const model = parts.slice(1).join(' ');
    return { make, model, year: '' };
  }
};

export interface SubstitutionUser {
  id: string;
  name: string | null;
  email: string | null;
  phone: string | null;
  profilePicture?: string | null;
}

export interface SubstitutionRequest {
  id: string;
  bookingId: string;
  requestedBy?: SubstitutionUser; // May not be returned by backend
  requestedByType?: 'Host' | 'Admin'; // May not be returned by backend
  host: SubstitutionUser; // Required - always in response
  guest: SubstitutionUser; // Required - always in response
  originalVehicle: SubstitutionVehicle;
  substituteVehicle: SubstitutionVehicle;
  reason: string;
  status: 'pending' | 'accepted' | 'declined' | 'expired';
  guestResponseDeadline: string;
  guestResponse?: {
    responded: boolean;
    responseDate?: string;
    decision?: 'accept' | 'decline';
  };
  adminOverride?: {
    overriddenBy: string;
    overriddenAt: string;
    overrideReason: string;
    action: 'approve' | 'reject';
  };
  createdBy?: string; // Admin who created it
  createdAt: string;
  updatedAt: string;
}

export interface SubstitutionRequestsResponse {
  success: boolean;
  data: {
    requests: SubstitutionRequest[];
    pagination: {
      total: number;
      page: number;
      limit: number;
      totalPages: number;
    };
  };
  error?: string;
}

export interface SubstitutionAnalytics {
  period: {
    startDate: string;
    endDate: string;
  };
  overview: {
    totalRequests: number;
    pending: number;
    accepted: number;
    declined: number;
    expired: number;
    acceptanceRate: number;
    averageResponseTime: number; // in hours
  };
  adminOverrides: Array<{
    adminId: string;
    adminName: string;
    count: number;
    action?: string;
  }>;
  topHosts: Array<{
    hostId: string;
    hostName: string;
    requestCount: number;
    acceptanceRate: number;
  }>;
  dailyTrend: Array<{
    date: string;
    requests: number;
    accepted: number;
    declined: number;
  }>;
  commonReasons: Array<{
    reason: string;
    count: number;
    percentage: number;
  }>;
}

export const substitutionService = {
  /**
   * 📋 Get all substitution requests with filters
   */
  async getSubstitutionRequests(filters?: {
    status?: 'pending' | 'accepted' | 'declined' | 'expired' | 'all';
    page?: number;
    limit?: number;
    hostId?: string;
    guestId?: string;
    bookingId?: string;
    search?: string;
    startDate?: string;
    endDate?: string;
    sortBy?: 'created_at' | 'guest_response_deadline';
    sortOrder?: 'asc' | 'desc';
  }): Promise<SubstitutionRequestsResponse> {
    try {
      const queryParams = new URLSearchParams();

      if (filters?.status && filters.status !== 'all') queryParams.append('status', filters.status);
      if (filters?.page) queryParams.append('page', filters.page.toString());
      if (filters?.limit) queryParams.append('limit', filters.limit.toString());
      if (filters?.hostId) queryParams.append('hostId', filters.hostId);
      if (filters?.guestId) queryParams.append('guestId', filters.guestId);
      if (filters?.bookingId) queryParams.append('bookingId', filters.bookingId);
      if (filters?.search) queryParams.append('search', filters.search);
      if (filters?.startDate) queryParams.append('startDate', filters.startDate);
      if (filters?.endDate) queryParams.append('endDate', filters.endDate);
      if (filters?.sortBy) queryParams.append('sortBy', filters.sortBy);
      if (filters?.sortOrder) queryParams.append('sortOrder', filters.sortOrder);

      const url = `${getApiBaseUrl()}/api/v1/admin/substitution-requests${queryParams.toString() ? `?${queryParams}` : ''}`;

      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error(`❌ Substitution Requests API Error ${response.status}:`, errorText);
        throw new Error(`API Error ${response.status}: ${response.statusText}`);
      }

      const result = await response.json();

      return result;
    } catch (error) {
      console.error('❌ Error fetching substitution requests:', error);

      return {
        success: false,
        data: {
          requests: [],
          pagination: {
            total: 0,
            page: 1,
            limit: 50,
            totalPages: 0
          }
        },
        error: error instanceof Error ? error.message : 'Failed to fetch substitution requests'
      };
    }
  },

  /**
   * 🔍 Get detailed information for a specific substitution request
   */
  async getSubstitutionRequestById(requestId: string): Promise<{
    success: boolean;
    data?: SubstitutionRequest;
    error?: string;
  }> {
    try {
      const url = `${getApiBaseUrl()}/api/v1/admin/substitution-requests/${requestId}`;

      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error(`❌ Get Request Details API Error ${response.status}:`, errorText);
        throw new Error(`API Error ${response.status}: ${response.statusText}`);
      }

      const result = await response.json();

      return result;
    } catch (error) {
      console.error('❌ Error fetching request details:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to fetch request details'
      };
    }
  },

  /**
   * ⚡ Admin override - Force approve or reject a substitution request
   */
  async overrideSubstitutionRequest(
    requestId: string,
    action: 'approve' | 'reject',
    adminId: string,
    reason: string
  ): Promise<{
    success: boolean;
    data?: SubstitutionRequest;
    message?: string;
    error?: string;
  }> {
    try {
      const url = `${getApiBaseUrl()}/api/v1/admin/substitution-requests/${requestId}/override`;

      const response = await fetch(url, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          action,
          adminId,
          reason
        })
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error(`❌ Override Request API Error ${response.status}:`, errorText);
        throw new Error(`API Error ${response.status}: ${response.statusText}`);
      }

      const result = await response.json();

      return result;
    } catch (error) {
      console.error('❌ Error overriding substitution request:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to override request'
      };
    }
  },

  /**
   * ➕ Create substitution request on behalf of host (admin-initiated)
   */
  async createSubstitutionRequest(
    bookingId: string,
    substituteVehicleId: string,
    reason: string,
    adminId: string
  ): Promise<{
    success: boolean;
    data?: SubstitutionRequest;
    message?: string;
    error?: string;
  }> {
    try {
      const url = `${getApiBaseUrl()}/api/v1/admin/bookings/${bookingId}/substitution-request`;

      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          substituteVehicleId,
          reason,
          adminId
        })
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error(`❌ Create Request API Error ${response.status}:`, errorText);
        throw new Error(`API Error ${response.status}: ${response.statusText}`);
      }

      const result = await response.json();

      return result;
    } catch (error) {
      console.error('❌ Error creating substitution request:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to create substitution request'
      };
    }
  },

  /**
   * 📊 Get analytics and dashboard stats
   */
  async getAnalytics(filters?: {
    startDate?: string;
    endDate?: string;
  }): Promise<{
    success: boolean;
    data?: SubstitutionAnalytics;
    error?: string;
  }> {
    try {
      const queryParams = new URLSearchParams();

      if (filters?.startDate) queryParams.append('startDate', filters.startDate);
      if (filters?.endDate) queryParams.append('endDate', filters.endDate);

      const url = `${getApiBaseUrl()}/api/v1/admin/substitution-requests/analytics${queryParams.toString() ? `?${queryParams}` : ''}`;

      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error(`❌ Analytics API Error ${response.status}:`, errorText);
        throw new Error(`API Error ${response.status}: ${response.statusText}`);
      }

      const result = await response.json();

      return result;
    } catch (error) {
      console.error('❌ Error fetching analytics:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to fetch analytics'
      };
    }
  }
};
